import { useRef, useCallback } from "react";

const RECOVERY_RETRY_DELAYS_MS = [180, 800, 1800];
const MANUAL_SEEK_GUARD_MS = 950;
const MANUAL_SEEK_GUARD_WINDOW_MS = 1500;

export function usePlaybackRecovery({
  audioRef,
  playTokenRef,
  playbackIntentRef,
  isPausedRef,
  isPlayingRef,
  currentChapterIndexRef,
  currentIndexRef,
  queuedOffsetRef,
  pauseOriginRef,
  aiAudioAvailable,
  supportsAudio,
  supportsSpeechFallback,
  recoveryAttemptRef,
  recoveryInFlightRef,
  shouldRecoverPlaybackRef,
  manualSeekGuardUntilRef,
  manualSeekShouldResumeRef,
  browserFallbackForcedRef,
  speechUtteranceRef,
  startAiChapterPlayback,
  speakFromIndexRef,
  suppressAiPlaybackForSession,
  resumeProgressTrackingRef,
  freezeProgressTrackingRef,
  setPlaybackPhaseState,
  setIsPlaying,
  setIsPaused,
  setStatus,
  content,
  canUseBrowserSpeechFallback
}) {
  const recoveryTimeoutRef = useRef(null);
  const fallbackContinuationTimeoutRef = useRef(null);
  const seekResumeTimeoutRef = useRef(null);
  const manualSeekTimeoutRef = useRef(null);

  const clearRecoveryTimeout = useCallback(() => {
    if (recoveryTimeoutRef.current) {
      window.clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
  }, []);

  const clearFallbackContinuationTimeout = useCallback(() => {
    if (fallbackContinuationTimeoutRef.current) {
      window.clearTimeout(fallbackContinuationTimeoutRef.current);
      fallbackContinuationTimeoutRef.current = null;
    }
  }, []);

  const clearSeekResumeTimeout = useCallback(() => {
    if (seekResumeTimeoutRef.current) {
      window.clearTimeout(seekResumeTimeoutRef.current);
      seekResumeTimeoutRef.current = null;
    }
  }, []);

  const clearManualSeekTimeout = useCallback(() => {
    if (manualSeekTimeoutRef.current) {
      window.clearTimeout(manualSeekTimeoutRef.current);
      manualSeekTimeoutRef.current = null;
    }
  }, []);

  const cancelAll = useCallback(() => {
    clearRecoveryTimeout();
    clearFallbackContinuationTimeout();
    clearSeekResumeTimeout();
    clearManualSeekTimeout();
  }, [
    clearFallbackContinuationTimeout,
    clearManualSeekTimeout,
    clearRecoveryTimeout,
    clearSeekResumeTimeout
  ]);

  const clearManualSeekGuard = useCallback(() => {
    manualSeekGuardUntilRef.current = 0;
    manualSeekShouldResumeRef.current = false;
    clearManualSeekTimeout();
  }, [clearManualSeekTimeout, manualSeekGuardUntilRef, manualSeekShouldResumeRef]);

  const isManualSeekActive = useCallback(() => {
    return manualSeekGuardUntilRef.current > Date.now();
  }, [manualSeekGuardUntilRef]);

  const schedulePlaybackRecovery = useCallback((delay = RECOVERY_RETRY_DELAYS_MS[0]) => {
    clearRecoveryTimeout();

    recoveryTimeoutRef.current = window.setTimeout(async () => {
      const audio = audioRef.current;
      recoveryTimeoutRef.current = null;

      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }

      if (
        recoveryInFlightRef.current ||
        !aiAudioAvailable ||
        !supportsAudio ||
        !playbackIntentRef.current ||
        isPausedRef.current ||
        !audio
      ) {
        return;
      }

      if (!audio.src || !audio.dataset.cacheKey) {
        recoveryInFlightRef.current = true;
        setPlaybackPhaseState("loading", {
          source: "recovery-missing-audio",
          chapterIndex: currentChapterIndexRef.current
        });
        setStatus(content.statusBuffering);

        try {
          const token = playTokenRef.current + 1;
          playTokenRef.current = token;

          await startAiChapterPlayback({
            chapterIndex: currentChapterIndexRef.current,
            blockIndex: currentIndexRef.current,
            offsetChars: queuedOffsetRef.current,
            token,
            source: "recovery-missing-audio"
          });
        } catch (error) {
          if (
            playbackIntentRef.current &&
            !isPausedRef.current &&
            supportsSpeechFallback &&
            canUseBrowserSpeechFallback(error)
          ) {
            suppressAiPlaybackForSession(error, "recovery-missing-audio");
            setStatus(content.statusAiFallback);
            speakFromIndexRef.current(currentIndexRef.current, {
              offsetChars: queuedOffsetRef.current,
              forceBrowserFallback: true,
              source: "recovery-missing-audio-fallback"
            });
            return;
          }

          const nextAttemptIndex = Math.min(
            recoveryAttemptRef.current + 1,
            RECOVERY_RETRY_DELAYS_MS.length - 1
          );

          recoveryAttemptRef.current = nextAttemptIndex;

          if (playbackIntentRef.current && !isPausedRef.current) {
            schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[nextAttemptIndex]);
          }
        } finally {
          recoveryInFlightRef.current = false;
        }

        return;
      }

      if (!audio.paused && !audio.ended) {
        shouldRecoverPlaybackRef.current = false;
        recoveryAttemptRef.current = 0;
        resumeProgressTrackingRef.current();
        setPlaybackPhaseState("playing", {
          source: "recovery-confirmed",
          chapterIndex: currentChapterIndexRef.current
        });
        return;
      }

      recoveryInFlightRef.current = true;

      try {
        if (audio.readyState < 2) {
          shouldRecoverPlaybackRef.current = true;
          setPlaybackPhaseState("loading", {
            source: "recovery-reload",
            chapterIndex: currentChapterIndexRef.current
          });
          audio.load();
          return;
        }

        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === "function") {
          await playPromise;
        }

        if (playbackIntentRef.current && !isPausedRef.current) {
          shouldRecoverPlaybackRef.current = false;
          recoveryAttemptRef.current = 0;
          pauseOriginRef.current = "none";
          resumeProgressTrackingRef.current();
          setIsPlaying(true);
          setIsPaused(false);
          setPlaybackPhaseState("playing", {
            source: "recovery-resumed",
            chapterIndex: currentChapterIndexRef.current
          });
        }
      } catch (_error) {
        const nextAttemptIndex = Math.min(
          recoveryAttemptRef.current + 1,
          RECOVERY_RETRY_DELAYS_MS.length - 1
        );
        recoveryAttemptRef.current = nextAttemptIndex;
        setPlaybackPhaseState("pausedBySystem", {
          source: "recovery-retry",
          chapterIndex: currentChapterIndexRef.current,
          attempt: nextAttemptIndex + 1
        });

        if (playbackIntentRef.current && !isPausedRef.current) {
          schedulePlaybackRecovery(RECOVERY_RETRY_DELAYS_MS[nextAttemptIndex]);
        }
      } finally {
        recoveryInFlightRef.current = false;
      }
    }, delay);
  }, [
    aiAudioAvailable,
    audioRef,
    canUseBrowserSpeechFallback,
    content,
    currentChapterIndexRef,
    currentIndexRef,
    isPausedRef,
    pauseOriginRef,
    playTokenRef,
    playbackIntentRef,
    queuedOffsetRef,
    recoveryAttemptRef,
    recoveryInFlightRef,
    resumeProgressTrackingRef,
    setIsPaused,
    setIsPlaying,
    setPlaybackPhaseState,
    setStatus,
    shouldRecoverPlaybackRef,
    speakFromIndexRef,
    startAiChapterPlayback,
    suppressAiPlaybackForSession,
    supportsAudio,
    supportsSpeechFallback,
    clearRecoveryTimeout
  ]);

  const startManualSeekGuard = useCallback(({ shouldResume = false } = {}) => {
    manualSeekGuardUntilRef.current = Date.now() + MANUAL_SEEK_GUARD_WINDOW_MS;
    manualSeekShouldResumeRef.current = Boolean(shouldResume);
    clearManualSeekTimeout();

    manualSeekTimeoutRef.current = window.setTimeout(() => {
      const shouldResumePlayback =
        manualSeekShouldResumeRef.current &&
        playbackIntentRef.current &&
        !isPausedRef.current &&
        Boolean(audioRef.current) &&
        audioRef.current.paused &&
        !audioRef.current.ended;

      clearManualSeekGuard();

      if (shouldResumePlayback) {
        schedulePlaybackRecovery(80);
      }
    }, MANUAL_SEEK_GUARD_MS);
  }, [
    audioRef,
    clearManualSeekGuard,
    clearManualSeekTimeout,
    isPausedRef,
    manualSeekGuardUntilRef,
    manualSeekShouldResumeRef,
    playbackIntentRef,
    schedulePlaybackRecovery
  ]);

  const scheduleSeekResume = useCallback((blockIndex, offsetChars) => {
    clearSeekResumeTimeout();
    seekResumeTimeoutRef.current = window.setTimeout(() => {
      seekResumeTimeoutRef.current = null;
      speakFromIndexRef.current(blockIndex, { offsetChars });
    }, 80);
  }, [clearSeekResumeTimeout, speakFromIndexRef]);

  const makeFallbackContinuationGuard = useCallback((token, utterance) => {
    let didFinalize = false;

    const schedule = (delayMs) => {
      clearFallbackContinuationTimeout();
      fallbackContinuationTimeoutRef.current = window.setTimeout(() => {
        if (
          didFinalize ||
          playTokenRef.current !== token ||
          speechUtteranceRef.current !== utterance
        ) {
          return;
        }

        if (
          window.speechSynthesis.speaking ||
          window.speechSynthesis.pending
        ) {
          schedule(500);
          return;
        }

        finalize("watchdog");
      }, delayMs);
    };

    const finalize = () => {
      if (didFinalize || playTokenRef.current !== token) {
        return false;
      }

      didFinalize = true;
      clearFallbackContinuationTimeout();
      speechUtteranceRef.current = null;
      return true;
    };

    return { schedule, finalize };
  }, [clearFallbackContinuationTimeout, playTokenRef, speechUtteranceRef]);

  return {
    cancelAll,
    schedulePlaybackRecovery,
    startManualSeekGuard,
    isManualSeekActive,
    clearManualSeekGuard,
    scheduleSeekResume,
    makeFallbackContinuationGuard,
    clearRecoveryTimeout,
    clearFallbackContinuationTimeout,
    clearSeekResumeTimeout,
    clearManualSeekTimeout
  };
}
